import React, { useState, useEffect, useRef } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { generateRandomString } from 'js/helpers/header';
import { toast } from "react-hot-toast";
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
    fetchDeviceSettings,
    fetchWorkerSignInHistory,
    updateWorkerSignInSetting
} from 'js/Api/index';

const ExpandableText = ({ text, maxLines = 2 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showToggle, setShowToggle] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
        if (textRef.current) {
            const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight);
            const height = textRef.current.scrollHeight;
            const lines = Math.ceil(height / lineHeight);
            setShowToggle(lines > maxLines);
        }
    }, [text, maxLines]);

    if (!text || text.trim() === '') {
        return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>No notes</span>;
    }

    return (
        <div>
            <div
                ref={textRef}
                style={{
                    display: '-webkit-box',
                    WebkitLineClamp: isExpanded ? 'none' : maxLines,
                    WebkitBoxOrient: 'vertical',
                    overflow: isExpanded ? 'visible' : 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: '1.4',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap'
                }}
            >
                {text}
            </div>
            {showToggle && (
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#4285F4',
                        cursor: 'pointer',
                        fontSize: '12px',
                        padding: '2px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        marginTop: '4px',
                        fontWeight: '500'
                    }}
                    type="button"
                >
                    {isExpanded ? (
                        <>
                            Show less <ExpandLessIcon style={{ fontSize: '16px' }} />
                        </>
                    ) : (
                        <>
                            Show more <ExpandMoreIcon style={{ fontSize: '16px' }} />
                        </>
                    )}
                </button>
            )}
        </div>
    );
};

const WorkerHistoryTab = ({ user, isFamilyorg }) => {
    const [workerHistory, setWorkerHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isEnabled, setIsEnabled] = useState(false);
    const [settingsData, setSettingsData] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const refreshTimerRef = useRef(null);

    useEffect(() => {
        loadWorkerSignInSettings();
        loadWorkerHistory();
        // 设置每60秒自动刷新
        refreshTimerRef.current = setInterval(() => {
            loadWorkerHistory();
        }, 60000);

        // 组件卸载时清理定时器
        return () => {
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
            }
        };
    }, []);

    const loadWorkerHistory = async () => {
        setLoading(true);
        setIsRefreshing(true);

        const result = await fetchWorkerSignInHistory(user.id);

        if (result.success) {
            setWorkerHistory(result.data);
        } else {
            console.error("Failed to fetch data");
            toast.error("Failed to load data");
            setWorkerHistory([]);
        }

        setLoading(false);
        setIsRefreshing(false);
    };

    const loadWorkerSignInSettings = async () => {
        const result = await fetchDeviceSettings(user.id);

        if (result.success) {
            setSettingsData(result.data);


            const workerSignInSetting = result.data.find(
                setting => setting.friendlyName === "settings.worker_signin"
            );

            if (workerSignInSetting) {

                if (workerSignInSetting.value === "true") {
                    setIsEnabled(true);
                } else if (workerSignInSetting.value === "false") {
                    setIsEnabled(false);
                } else {

                    setIsEnabled(false);
                }
            }
        } else {
            console.error("Failed to fetch settings");
            toast.error("Failed to load settings");
        }
    };

    const formatDateTime = (timeString) => {
        if (!timeString) return 'N/A';
        const utcTimeString = timeString.endsWith('Z') ? timeString : `${timeString}Z`;
        const date = new Date(utcTimeString);

        return `${date
            .toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            })
            .replace(" ", "")} on ${date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            })}`;
    };

    const handleEnableToggle = async (event) => {
        const newEnabled = event.target.checked;
        await updateSignInStatus(newEnabled);
    };

    const updateSignInStatus = async (status) => {
        try {

            if (!settingsData) {
                throw new Error("Settings data not available");
            }

            const workerSignInSetting = settingsData.find(
                setting => setting.friendlyName === "settings.worker_signin"
            );

            if (!workerSignInSetting) {
                throw new Error("setting not found");
            }


            const result = await updateWorkerSignInSetting(
                workerSignInSetting,
                user.id,
                status
            );

            if (result.success) {

                setIsEnabled(status);
                toast.success(result.message);


                loadWorkerHistory();
            } else {
                toast.error(result.error);
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error(`Failed to ${status ? "enable" : "disable"} status`);
        }
    };


    const handleRefresh = () => {
        loadWorkerHistory();
    };


    const handleImageClick = (entry, imageType = 'signin') => {
        setSelectedImage({
            ...entry,
            imageType,
            currentImageUrl: imageType === 'signin' ? entry.file_name : entry.signout_file_name
        });
    };

    return (
        <div style={{ padding: '20px' }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                    paddingBottom: "10px",
                    position: "relative"
                }}
            >
                <h3 style={{
                    margin: 0,
                    position: "absolute",
                    left: "45%",
                    transform: "translateX(-40%)",
                    width: "fit-content"
                }}>{isFamilyorg === true ? "VISITOR BOOK" : "WORKER HISTORY"}</h3>

                <div
                    style={{
                        position: "absolute",
                        right: "0px",
                    }}>

                </div>
            </div>

            <table className="table table-hover">
                <thead>
                    <tr>
                        <th style={{ width: "15%" }} >Name</th>
                        <th style={{ width: "12%" }}>Sign-in Time</th>
                        <th style={{ width: "10%", textAlign: "center" }}>Sign-in Photo</th>
                        <th style={{ width: "12%" }}>Sign-out Time</th>
                        <th style={{ width: "10%", textAlign: "center" }}>Sign-out Photo</th>
                        <th>Shift notes</th>
                        <th style={{ width: "15%" }}>Staff checklist submitted?</th>

                        <th style={{ width: "5%" }}>
                            <button
                                onClick={handleRefresh}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#4285F4",
                                    fontSize: "14px",
                                    padding: "5px",
                                    borderRadius: "4px",
                                    transition: "background-color 0.3s",
                                }}
                                disabled={isRefreshing}
                                title="Refresh"
                            >
                                <RefreshIcon
                                    style={{
                                        fontSize: "20px",
                                        animation: isRefreshing ? "spin 1s linear infinite" : "none"
                                    }}
                                />
                                <style jsx>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan="7" className="text-center">
                                Loading {isFamilyorg ? "visitor book" : "worker history"}...
                            </td>
                        </tr>
                    ) : workerHistory.length === 0 ? (
                        <tr>
                            <td colSpan="7" className="text-center">
                                No {isFamilyorg ? "visitor book" : "worker history"} found
                            </td>
                        </tr>
                    ) : (
                        workerHistory.map((entry) => (
                            <tr key={entry.id}>
                                <td style={{ verticalAlign: "middle" }}>
                                    {entry.staff_name}
                                </td>
                                <td style={{ verticalAlign: "middle" }}>
                                    {formatDateTime(entry.created_at)}
                                </td>

                                <td style={{ textAlign: "center" }}>
                                    <div
                                        onClick={() => handleImageClick(entry, 'signin')}
                                        style={{ cursor: 'pointer', verticalAlign: "middle" }}
                                    >
                                        <img
                                            src={entry.file_name}
                                            alt={`${entry.staff_name} sign-in`}
                                            onError={(e) => {
                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2U5ZTllOSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIGZhaWxlZCB0byBsb2FkPC90ZXh0Pjwvc3ZnPg==';
                                            }}
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'cover',
                                                borderRadius: '4px'
                                            }}
                                        />
                                    </div>
                                </td>
                                <td style={{ verticalAlign: "middle" }}>
                                    {formatDateTime(entry.signout_time)}
                                </td>
                                <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                                    {entry.signout_file_name ? (
                                        <div
                                            onClick={() => handleImageClick(entry, 'signout')}
                                            style={{ cursor: 'pointer', verticalAlign: "middle" }}
                                        >
                                            <img
                                                src={entry.signout_file_name}
                                                alt={`${entry.staff_name} sign-out`}
                                                onError={(e) => {
                                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2U5ZTllOSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIGZhaWxlZCB0byBsb2FkPC90ZXh0Pjwvc3ZnPg==';
                                                }}
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    objectFit: 'cover',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <span >No photo</span>
                                    )}
                                </td>
                                <td>
                                    <ExpandableText text={entry?.shiftNotes} maxLines={2} />

                                </td>
                                <td>{entry?.hasShiftChecklist === true && "Yes"}
                                    {entry?.hasShiftChecklist === false && "No"}</td>
                                <td></td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            <Modal
                open={!!selectedImage}
                onClose={() => setSelectedImage(null)}
                aria-labelledby="image-modal"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '60%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: '150vh',
                    maxHeight: '90vh',
                    bgcolor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Header with info */}
                    <div style={{
                        padding: '15px 20px',
                    }}>
                        <table className="table" style={{ margin: 0 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: "150px" }}>Type</th>
                                    <th style={{ width: "200px" }}>Date and time</th>
                                    <th>Name</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedImage && (
                                    <tr
                                        style={{
                                            transition: 'background-color 0.3s',
                                            borderColor: 'transparent !important'
                                        }}
                                    >
                                        <td style={{ verticalAlign: "middle" }}>
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                backgroundColor: selectedImage.imageType === 'signin' ? '#e3f2fd' : '#fff3e0',
                                                color: selectedImage.imageType === 'signin' ? '#1976d2' : '#f57c00'
                                            }}>
                                                {selectedImage.imageType === 'signin' ? 'SIGN-IN' : 'SIGN-OUT'}
                                            </span>
                                        </td>
                                        <td style={{ verticalAlign: "middle" }}>
                                            {selectedImage.imageType === 'signin'
                                                ? formatDateTime(selectedImage.created_at)
                                                : formatDateTime(selectedImage.signout_time)
                                            }
                                        </td>
                                        <td style={{ verticalAlign: "middle" }}>
                                            {selectedImage.staff_name}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Image Container */}
                    <div style={{
                        flex: 1,
                        overflow: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        maxHeight: 'calc(90vh - 140px)' // Subtract header and footer height
                    }}>
                        <img
                            src={selectedImage?.currentImageUrl}
                            alt={`${selectedImage?.staff_name} ${selectedImage?.imageType}`}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                display: 'block'
                            }}
                            onError={(e) => {
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2U5ZTllOSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIGZhaWxlZCB0byBsb2FkPC90ZXh0Pjwvc3ZnPg==';
                            }}
                        />
                    </div>

                    {/* Close button */}
                    <div style={{
                        textAlign: 'center',
                        padding: '15px',
                        borderTop: '1px solid #E9ECEF'
                    }}>
                        <button
                            onClick={() => setSelectedImage(null)}
                            type='button'
                            className="btn btn-outline-info btn-lg px-4 me-sm-3 fw-bold"
                        >
                            Close window
                        </button>
                    </div>
                </Box>
            </Modal>
        </div>
    );
};

export default WorkerHistoryTab;